import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, RefreshCw, Unplug, CheckCircle, AlertCircle, Clock, Plus, ShieldCheck, Link, Copy, RotateCcw } from 'lucide-react';
import { calendarService, CalendarConnection } from '@/services/api/calendar.service';
import { formatRelativeTime } from '@/utils/date';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Calendar integration management — Google Calendar connections + ICS feed.
 * Rendered both as a standalone page (calendar-settings.tsx) and inside the
 * Settings "Calendar" tab, so it owns its own state and an inline connect
 * action rather than relying on the page header.
 */
export function CalendarSettingsPanel({ showHeader = true }: { showHeader?: boolean }) {
  const { t } = useTranslation('calendar');
  const { toast } = useToast();
  const [connections, setConnections] = useState<CalendarConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    loadConnections();

    // Check for OAuth callback status in URL
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const message = params.get('message');

    if (status === 'success') {
      toast({
        title: t('toast.connectSuccess'),
        description: message || t('toasts.connectedSuccess'),
      });
      window.history.replaceState({}, '', window.location.pathname);
      loadConnections();
    } else if (status === 'error') {
      toast({
        title: t('toast.connectError'),
        description: message || t('toasts.connectError'),
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await calendarService.getConnections();
      setConnections(data);
    } catch (error: any) {
      console.error('[CalendarSettings] Failed to load connections:', error);
      toast({
        title: t('toast.connectError'),
        description: error.message || t('toasts.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const { authUrl } = await calendarService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error: any) {
      setConnecting(false);
      toast({
        title: t('toast.connectError'),
        description: error.message || t('toasts.oauthError'),
        variant: 'destructive',
      });
    }
  };

  const handleSync = async (connectionId: number) => {
    try {
      const stats = await calendarService.syncNow(connectionId);
      toast({
        title: t('toast.syncSuccess'),
        description: t('toasts.syncStats', {
          created: stats.created,
          updated: stats.updated,
          deleted: stats.deleted,
          defaultValue: '{{created}} created, {{updated}} updated, {{deleted}} deleted',
        }),
      });
      loadConnections();
    } catch (error: any) {
      toast({
        title: t('toast.syncError'),
        description: error.message || t('toasts.syncError'),
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = async (connectionId: number) => {
    try {
      await calendarService.disconnect(connectionId);
      toast({
        title: t('toast.disconnectSuccess'),
      });
      loadConnections();
    } catch (error: any) {
      toast({
        title: t('toast.disconnectError'),
        description: error.message || t('toasts.disconnectError'),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-soft" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + connect action. Hidden on the standalone page, which already
          renders title/subtitle via the page header. */}
      {showHeader ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink">{t('title')}</h3>
            <p className="text-body-xs text-soft mt-1 leading-relaxed">{t('subtitle')}</p>
          </div>
          {connections.length > 0 && (
            <Button onClick={handleConnect} disabled={connecting} size="md" iconLeft={<Plus className="h-4 w-4" />} className="flex-shrink-0">
              {t('connectButton')}
            </Button>
          )}
        </div>
      ) : (
        connections.length > 0 && (
          <div className="flex justify-end">
            <Button onClick={handleConnect} disabled={connecting} size="md" iconLeft={<Plus className="h-4 w-4" />} className="flex-shrink-0">
              {t('connectButton')}
            </Button>
          </div>
        )
      )}

      {/* Privacy Notice */}
      <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success-soft p-4">
        <ShieldCheck className="h-5 w-5 text-success-text mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-success-text">
            {t('privacyNotice.title')}
          </p>
          <p className="text-sm text-success-text/80 mt-1">
            {t('privacyNotice.description')}
          </p>
        </div>
      </div>

      {/* Empty State */}
      {connections.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Calendar className="h-16 w-16 mx-auto text-soft mb-4" />
            <h3 className="text-h2 text-ink mb-2">{t('emptyState.title')}</h3>
            <p className="text-soft mb-6">{t('emptyState.description')}</p>
            <Button onClick={handleConnect} disabled={connecting}>
              <Plus className="h-4 w-4 mr-2" />
              {t('emptyState.action')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Connections Grid */}
      {connections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connections.map((connection) => (
            <CalendarConnectionCard
              key={connection.id}
              connection={connection}
              onSync={handleSync}
              onDisconnect={handleDisconnect}
            />
          ))}
        </div>
      )}

      {/* ICS Feed */}
      <IcsFeedSection />
    </div>
  );
}

function IcsFeedSection() {
  const { t } = useTranslation('calendar');
  const { toast } = useToast();
  const [icsUrl, setIcsUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  useEffect(() => {
    calendarService.getIcsFeedUrl()
      .then(setIcsUrl)
      .catch(() => toast({ title: t('icsFeed.toast.loadError'), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = () => {
    if (!icsUrl) return;
    navigator.clipboard.writeText(icsUrl);
    setCopied(true);
    toast({ title: t('icsFeed.toast.copied') });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    setShowRegenerateDialog(false);
    setRegenerating(true);
    try {
      const newUrl = await calendarService.regenerateIcsFeedToken();
      setIcsUrl(newUrl);
      toast({ title: t('icsFeed.toast.regenerateSuccess') });
    } catch {
      toast({ title: t('icsFeed.toast.regenerateError'), variant: 'destructive' });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Link className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>{t('icsFeed.title')}</CardTitle>
              <CardDescription>{t('icsFeed.description')}</CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-soft">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">{t('icsFeed.urlLabel')}</p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={icsUrl || ''}
                  className="font-mono text-xs"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button variant="outline" size="icon" onClick={handleCopy} title={t('icsFeed.copyButton')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {copied && (
                <p className="text-xs text-green-600">{t('icsFeed.copied')}</p>
              )}
            </div>
          )}
        </CardContent>

        <CardFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRegenerateDialog(true)}
            disabled={loading || regenerating}
            className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            <RotateCcw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            {t('icsFeed.regenerateButton')}
          </Button>
          <p className="text-xs text-soft ml-3">{t('icsFeed.regenerateWarning')}</p>
        </CardFooter>
      </Card>

      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('icsFeed.regenerateButton')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirm.regenerateToken')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate} className="bg-destructive text-destructive-foreground">
              {t('icsFeed.regenerateButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface CalendarConnectionCardProps {
  connection: CalendarConnection;
  onSync: (connectionId: number) => Promise<void>;
  onDisconnect: (connectionId: number) => Promise<void>;
}

function CalendarConnectionCard({ connection, onSync, onDisconnect }: CalendarConnectionCardProps) {
  const { t } = useTranslation('calendar');
  const [syncing, setSyncing] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync(connection.id);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    setShowDisconnectDialog(false);
    await onDisconnect(connection.id);
  };

  const getStatusBadge = () => {
    if (syncing) {
      return (
        <Badge tone="warn">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          {t('connection.status.syncing')}
        </Badge>
      );
    }

    if (!connection.active) {
      return <Badge tone="neutral">{t('connection.status.inactive')}</Badge>;
    }

    if (connection.lastSyncError) {
      return (
        <Badge tone="danger">
          <AlertCircle className="h-3 w-3 mr-1" />
          {t('connection.status.failed')}
        </Badge>
      );
    }

    return (
      <Badge tone="success">
        <CheckCircle className="h-3 w-3 mr-1" />
        {t('connection.status.active')}
      </Badge>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>{connection.calendarName}</CardTitle>
                <CardDescription className="text-sm">{connection.calendarEmail}</CardDescription>
              </div>
            </div>
          </div>
          <div className="mt-3">{getStatusBadge()}</div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {/* Last Sync Time */}
            <div className="flex items-center gap-2 text-sm text-soft">
              <Clock className="h-4 w-4" />
              {connection.lastSyncedAt ? (
                <span>
                  {t('connection.lastSync', { time: formatRelativeTime(connection.lastSyncedAt) })}
                </span>
              ) : (
                <span>{t('connection.neverSynced')}</span>
              )}
            </div>

            {/* Sync Error */}
            {connection.lastSyncError && (
              <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
                <p className="text-sm text-danger-text">{connection.lastSyncError}</p>
                {connection.consecutiveFailures > 0 && (
                  <p className="text-xs text-danger-text/80 mt-1">
                    {t('connection.errors.consecutiveFailures', { count: connection.consecutiveFailures })}
                  </p>
                )}
              </div>
            )}

            {/* Sync Direction */}
            <div className="flex gap-2 text-xs">
              {connection.pullFromCalendar && (
                <Badge variant="secondary" className="text-xs">
                  Pull from Calendar
                </Badge>
              )}
              {connection.pushToCalendar && (
                <Badge variant="secondary" className="text-xs">
                  Push to Calendar
                </Badge>
              )}
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleSync}
            disabled={syncing || !connection.active}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {t('connection.syncNow')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => setShowDisconnectDialog(true)}
          >
            <Unplug className="h-4 w-4 mr-2" />
            {t('connection.disconnect')}
          </Button>
        </CardFooter>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dialogs.disconnectTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirm.disconnect')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} className="bg-destructive text-destructive-foreground">
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
